import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import { Input } from '../../../components/forms/Input';
import { Select } from '../../../components/forms/Select';
import { usePasStore } from '../pasStore';
import type { NewPolicyDraft, QuoteVariation } from '../types';
import { formatCurrency, todayStr } from '../utils';
import { Button } from './Button';
import { Modal } from './Modal';

/** Lime-green used to flag the sold plan in the quote variation picker. */
const SOLD_PLAN_GREEN = '#bef264';

/** Prefer the sold plan when defaulting the variation picker. */
const defaultVariationId = (list: QuoteVariation[] = []) =>
  (list.find((v) => v.isSoldPlan) ?? list[0])?.id ?? '';

export interface NewPolicyPrefill {
  quoteId?: string;
  quoteName?: string;
  prospectName?: string;
  contactName?: string;
  product?: string;
  effectiveDate?: string;
  variations?: QuoteVariation[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  prefill?: NewPolicyPrefill;
}

export const NewPolicyModal: React.FC<Props> = ({ isOpen, onClose, prefill }) => {
  const navigate = useNavigate();
  const createFromQuote = usePasStore((s) => s.createFromQuote);
  const approvedQuotes = usePasStore((s) => s.approvedQuotes);
  const brokers = usePasStore((s) => s.brokers);
  const carriers = usePasStore((s) => s.carriers);
  const products = usePasStore((s) => s.products);
  const referenceLoading = usePasStore((s) => s.referenceLoading);

  const hasPrefillVariations = !!prefill?.variations?.length;

  const [quoteId, setQuoteId] = useState(prefill?.quoteId ?? '');
  const selectedQuote = useMemo(
    () => approvedQuotes.find((q) => q.id === quoteId),
    [approvedQuotes, quoteId],
  );

  const variations = useMemo(
    () => (hasPrefillVariations ? prefill!.variations! : selectedQuote?.variations ?? []),
    [hasPrefillVariations, prefill, selectedQuote],
  );
  const [variationId, setVariationId] = useState(defaultVariationId(prefill?.variations));
  const variation = variations.find((v) => v.id === variationId) ?? variations[0];

  const [insuredName, setInsuredName] = useState(prefill?.prospectName ?? '');
  const [contactName, setContactName] = useState(prefill?.contactName ?? '');
  const [brokerId, setBrokerId] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(prefill?.effectiveDate ?? todayStr());
  const [termMonths, setTermMonths] = useState('12');
  const [product, setProduct] = useState(prefill?.product ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Products, sorted by name for the picker. Guard against non-string / non-array
  // payloads so a malformed reference response can't crash the modal.
  const productOptions = useMemo(
    () =>
      (Array.isArray(products) ? products : [])
        .filter((p): p is string => typeof p === 'string')
        .sort((a, b) => a.localeCompare(b))
        .map((p) => ({ value: p, label: p })),
    [products],
  );

  // No approved quotes to convert (and nothing was prefilled from a quote detail page).
  const showEmptyState = !hasPrefillVariations && !referenceLoading && approvedQuotes.length === 0;
  const referenceReady = brokers.length > 0 && carriers.length > 0;

  // Default the source quote (and its derived fields) once approved quotes arrive.
  useEffect(() => {
    if (hasPrefillVariations || quoteId || approvedQuotes.length === 0) return;
    const first = approvedQuotes[0];
    setQuoteId(first.id);
    setVariationId(defaultVariationId(first.variations));
    setInsuredName(first.prospectName);
    setContactName(first.contactName);
    if (first.policyEffectiveDate) setEffectiveDate(first.policyEffectiveDate);
  }, [approvedQuotes, hasPrefillVariations, quoteId]);

  // Keep a valid variation selected whenever the list changes.
  useEffect(() => {
    if (variations.length && !variations.some((v) => v.id === variationId)) {
      setVariationId(defaultVariationId(variations));
    }
  }, [variations, variationId]);

  // Default broker/carrier once reference data arrives.
  useEffect(() => {
    if (!brokerId && brokers.length) setBrokerId(brokers[0].id);
  }, [brokers, brokerId]);
  useEffect(() => {
    if (!carrierId && carriers.length) setCarrierId(carriers[0].id);
  }, [carriers, carrierId]);

  // Default the product from the prefill / source quote, falling back to the
  // first available product once the reference list arrives.
  useEffect(() => {
    if (product) return;
    const fallback = prefill?.product ?? selectedQuote?.product ?? productOptions[0]?.value ?? '';
    if (fallback) setProduct(fallback);
  }, [product, prefill, selectedQuote, productOptions]);

  const quoteOptions = useMemo(
    () => approvedQuotes.map((q) => ({ value: q.id, label: `${q.quoteName} — ${q.prospectName}` })),
    [approvedQuotes],
  );
  // The sold plan is called out in lime-green so it's obvious which variation
  // the client actually bought. Native <option> background styling is ignored by
  // some platforms, so the label carries the marker too.
  const variationOptions = useMemo(
    () =>
      variations.map((v) => ({
        value: v.id,
        label: v.isSoldPlan ? `${v.label} — Sold plan` : v.label,
        style: v.isSoldPlan
          ? ({ backgroundColor: SOLD_PLAN_GREEN, color: '#1a2e05', fontWeight: 700 } as const)
          : undefined,
      })),
    [variations],
  );

  const onQuoteChange = (id: string) => {
    setQuoteId(id);
    const quote = approvedQuotes.find((q) => q.id === id);
    if (quote) {
      setVariationId(defaultVariationId(quote.variations));
      setInsuredName(quote.prospectName);
      setContactName(quote.contactName);
      if (quote.product) setProduct(quote.product);
      if (quote.policyEffectiveDate) setEffectiveDate(quote.policyEffectiveDate);
    }
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!insuredName.trim()) next.insuredName = 'Insured name is required';
    if (!contactName.trim()) next.contactName = 'Contact name is required';
    if (!variation) next.variation = 'Select a quote variation';
    if (!product) next.product = 'Select a product';
    if (!effectiveDate) next.effectiveDate = 'Effective date is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || !variation || !referenceReady) return;
    setSubmitting(true);
    const draft: NewPolicyDraft = {
      insuredName: insuredName.trim(),
      contactName: contactName.trim(),
      brokerId,
      carrierId,
      product,
      effectiveDate,
      termMonths: Number(termMonths),
      annualPremium: variation.annualPremium,
      coverage: {
        benefitPercent: variation.benefitPercent,
        weeklyCap: variation.weeklyCap,
        lengthOfLeaveWeeks: variation.lengthOfLeaveWeeks,
        waitingPeriodDays: variation.waitingPeriodDays,
        aggregateLimit: variation.aggregateLimit,
        concurrency: variation.concurrency,
        sdiOffset: variation.sdiOffset,
        pflOffset: variation.pflOffset,
      },
      costs: variation.costs,
      sourceQuote: {
        quoteId: prefill?.quoteId ?? selectedQuote?.id ?? '',
        quoteName: prefill?.quoteName ?? selectedQuote?.quoteName ?? '',
        prospectName: insuredName.trim(),
        variationLabel: variation.label,
      },
    };
    try {
      const policy = await createFromQuote(draft);
      onClose();
      navigate(`/policy-admin/policies/${policy.id}`);
    } catch {
      // The store surfaces the error as a toast; keep the modal open so the user can retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create policy from quote"
      subtitle="Convert an approved quote variation into a draft policy and start the workflow."
      size="lg"
      footer={
        showEmptyState ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={submitting}
              disabled={!referenceReady || !variation}
              rightIcon={<FaArrowRight className="h-3 w-3" />}
            >
              Create draft policy
            </Button>
          </>
        )
      }
    >
      {showEmptyState ? (
        <div className="py-8 text-center">
          <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            No approved quotes available
          </p>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Approve a quote in the quoting tool, then create a policy from its detail page.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {!hasPrefillVariations ? (
              <Select label="Source quote" options={quoteOptions} value={quoteId} onChange={(e) => onQuoteChange(e.target.value)} fullWidth required />
            ) : (
              <Input label="Source quote" value={prefill?.quoteName ?? ''} readOnly fullWidth />
            )}
            <Select
              label="Quote variation"
              options={variationOptions}
              value={variationId}
              onChange={(e) => setVariationId(e.target.value)}
              errorText={errors.variation}
              helperText={variation?.isSoldPlan ? 'This is the sold plan on the source quote.' : undefined}
              style={
                variation?.isSoldPlan
                  ? { backgroundColor: SOLD_PLAN_GREEN, color: '#1a2e05', fontWeight: 700 }
                  : undefined
              }
              fullWidth
              required
            />
          </div>

          {variation && (
            <div
              className="grid grid-cols-2 gap-3 rounded-lg border p-4 sm:grid-cols-4"
              style={{ backgroundColor: 'var(--color-primary-50)', borderColor: 'var(--color-border-light)' }}
            >
              {[
                { label: 'Benefit', value: `${variation.benefitPercent}%` },
                { label: 'Weekly cap', value: formatCurrency(variation.weeklyCap) },
                { label: 'Leave', value: `${variation.lengthOfLeaveWeeks} wks` },
                { label: 'Annual premium', value: formatCurrency(variation.annualPremium) },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-primary-700)' }}>
                    {stat.label}
                  </p>
                  <p className="text-sm font-bold" style={{ color: 'var(--color-primary-800)' }}>
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          )}

          {variation?.costs && (
            <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-warning-50)', borderColor: 'var(--color-warning-100)' }}>
              <p className="mb-3 text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-warning-800)' }}>
                Total Costs
              </p>
              <dl className="space-y-2">
                {[
                  { label: 'Total Program Cost w/o Tax/Stamping/Issuance Fees', value: variation.costs.totalProgramCostWithoutFees },
                  { label: 'Tax/Stamping/Issuance Fees', value: variation.costs.taxStampingIssuanceFees },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between gap-4 text-sm">
                    <dt style={{ color: 'var(--color-warning-800)' }}>{row.label}</dt>
                    <dd className="font-semibold tabular-nums" style={{ color: 'var(--color-warning-800)' }}>{formatCurrency(row.value)}</dd>
                  </div>
                ))}
                <div className="mt-1 flex items-center justify-between gap-4 border-t pt-2 text-sm" style={{ borderColor: 'var(--color-warning-100)' }}>
                  <dt className="font-bold" style={{ color: 'var(--color-warning-800)' }}>Total Program Costs w/ Tax/Stamping/Issuance Fees</dt>
                  <dd className="text-base font-bold tabular-nums" style={{ color: 'var(--color-warning-800)' }}>{formatCurrency(variation.costs.totalProgramCostWithFees)}</dd>
                </div>
              </dl>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Named insured" value={insuredName} onChange={(e) => setInsuredName(e.target.value)} errorText={errors.insuredName} fullWidth required />
            <Input label="Primary contact" value={contactName} onChange={(e) => setContactName(e.target.value)} errorText={errors.contactName} fullWidth required />
            <Select label="Product" options={productOptions} value={product} onChange={(e) => setProduct(e.target.value)} errorText={errors.product} fullWidth required />
            <Select label="Producing broker" options={brokers.map((b) => ({ value: b.id, label: `${b.agency} — ${b.name}` }))} value={brokerId} onChange={(e) => setBrokerId(e.target.value)} fullWidth required />
            <Select label="Carrier" options={carriers.map((c) => ({ value: c.id, label: c.name }))} value={carrierId} onChange={(e) => setCarrierId(e.target.value)} fullWidth required />
            <Input label="Effective date" type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} errorText={errors.effectiveDate} fullWidth required />
            <Select label="Policy term" options={[{ value: '12', label: '12 months' }, { value: '24', label: '24 months' }]} value={termMonths} onChange={(e) => setTermMonths(e.target.value)} fullWidth required />
          </div>
        </div>
      )}
    </Modal>
  );
};
